import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { getUserClients } from "../lib/api";
import DirectMessageComposeModal from "../components/DirectMessageComposeModal";
import { useAuth } from "../contexts/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Loader2,
  ArrowLeft,
  Mail,
  Building2,
  Users as UsersIcon,
  Briefcase,
  DollarSign,
} from "lucide-react";

const lifecycleLabels = {
  onboarding: { label: "Onboarding", className: "bg-blue-100 text-blue-800" },
  active: { label: "Active", className: "bg-emerald-100 text-emerald-800" },
  monthly_active: { label: "Monthly Active", className: "bg-emerald-100 text-emerald-800" },
  pending_renewal: { label: "Pending Renewal", className: "bg-amber-100 text-amber-800" },
  archived: { label: "Archived", className: "bg-slate-100 text-slate-700" },
};

const formatMoney = (v) =>
  v == null ? "—" : `$${Number(v).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;

const formatDate = (d) =>
  !d ? "—" : new Date(d).toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" });

export default function RepDetailPage() {
  const { userId } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const { user: currentUser } = useAuth();
  const canSendDirect = currentUser?.role === "super_admin" || currentUser?.role === "manager" || currentUser?.role === "ops_coordinator";

  useEffect(() => {
    (async () => {
      try {
        const { data } = await getUserClients(userId);
        setData(data);
      } catch (e) {
        console.error("Failed to load rep clients:", e);
      } finally {
        setLoading(false);
      }
    })();
  }, [userId]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!data) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <p className="text-muted-foreground">User not found</p>
      </div>
    );
  }

  const { user, clients } = data;
  const totalMRR = clients.reduce(
    (sum, c) => sum + (Number(c.primary_project?.contract_value) || 0),
    0
  );
  const activeCount = clients.filter(
    (c) => c.primary_project && c.primary_project.lifecycle_stage !== "archived"
  ).length;

  return (
    <div className="space-y-6" data-testid="rep-detail-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center gap-2 mb-1">
            {user.agency_id && (
              <Link
                to={`/agencies/${user.agency_id}`}
                className="text-sm text-muted-foreground hover:text-primary inline-flex items-center gap-1"
                data-testid="rep-back-to-agency"
              >
                <ArrowLeft className="h-3.5 w-3.5" />
                {user.agency_name || "Back to agency"}
              </Link>
            )}
          </div>
          <h1 className="font-outfit text-3xl font-bold tracking-tight">{user.name}</h1>
          <div className="flex flex-wrap items-center gap-3 mt-2 text-sm text-muted-foreground">
            <span className="inline-flex items-center gap-1">
              <Mail className="h-3.5 w-3.5" />
              {user.email}
            </span>
            {user.agency_name && (
              <span className="inline-flex items-center gap-1">
                <Building2 className="h-3.5 w-3.5" />
                {user.agency_name}
              </span>
            )}
            <Badge variant="outline" className="capitalize">
              {user.role === "rep" ? "Sales Rep" : user.role}
            </Badge>
          </div>
        </div>
        {canSendDirect && (
          <DirectMessageComposeModal
            agencyId={user.agency_id}
            agencyName={user.agency_name}
            preselectUser={{ id: user.id, name: user.name || user.email, role: user.role }}
          />
        )}
      </div>

      {/* Stat strip */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
        <Card>
          <CardContent className="p-4 flex items-center justify-between">
            <div>
              <p className="text-xs text-muted-foreground">Total clients</p>
              <p className="text-2xl font-bold">{clients.length}</p>
            </div>
            <UsersIcon className="h-6 w-6 text-muted-foreground/60" />
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 flex items-center justify-between">
            <div>
              <p className="text-xs text-muted-foreground">Active</p>
              <p className="text-2xl font-bold text-emerald-600">{activeCount}</p>
            </div>
            <Briefcase className="h-6 w-6 text-muted-foreground/60" />
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 flex items-center justify-between">
            <div>
              <p className="text-xs text-muted-foreground">Approx. MRR</p>
              <p className="text-2xl font-bold">{formatMoney(totalMRR)}</p>
            </div>
            <DollarSign className="h-6 w-6 text-muted-foreground/60" />
          </CardContent>
        </Card>
      </div>

      {/* Client list */}
      <Card>
        <CardHeader>
          <CardTitle className="font-outfit flex items-center gap-2 text-lg">
            <UsersIcon className="h-5 w-5" />
            Clients
          </CardTitle>
        </CardHeader>
        <CardContent>
          {clients.length === 0 ? (
            <div className="py-12 text-center text-sm text-muted-foreground">
              No clients assigned to this rep yet.
            </div>
          ) : (
            <div className="space-y-3">
              {clients
                .slice()
                .sort((a, b) => (a.company || a.name || "").toLowerCase().localeCompare((b.company || b.name || "").toLowerCase()))
                .map((c) => {
                  const projects = c.projects && c.projects.length ? c.projects : (c.primary_project ? [c.primary_project] : []);
                  return (
                    <div key={c.id} className="rounded-md border" data-testid={`rep-client-row-${c.id}`}>
                      <div className="px-4 py-2 bg-secondary/40 border-b flex items-center justify-between">
                        <div>
                          <div className="font-semibold">{c.company || c.name}</div>
                          {c.email && (
                            <div className="text-xs text-muted-foreground truncate">{c.email}</div>
                          )}
                        </div>
                        <Badge variant="outline" className="text-xs">
                          {projects.length} order{projects.length === 1 ? "" : "s"}
                        </Badge>
                      </div>
                      <div className="divide-y">
                        {projects.length === 0 ? (
                          <div className="px-4 py-3 text-xs text-muted-foreground italic">No orders yet</div>
                        ) : projects.map((p) => {
                          const stage = p.lifecycle_stage;
                          const stageMeta = lifecycleLabels[stage];
                          return (
                            <div key={p.id} className="px-4 py-3 grid grid-cols-12 gap-3 items-center text-sm">
                              <div className="col-span-5 flex flex-wrap gap-1">
                                {(p.sub_product_names || []).slice(0, 4).map((sp, i) => (
                                  <Badge key={i} variant="outline" className="text-xs">{sp}</Badge>
                                ))}
                                {(p.sub_product_names || []).length > 4 && (
                                  <Badge variant="outline" className="text-xs">+{p.sub_product_names.length - 4}</Badge>
                                )}
                              </div>
                              <div className="col-span-2">
                                {stageMeta ? (
                                  <Badge className={stageMeta.className}>{stageMeta.label}</Badge>
                                ) : (
                                  <span className="text-xs text-muted-foreground">—</span>
                                )}
                              </div>
                              <div className="col-span-2 text-right tabular-nums">
                                {formatMoney(p.contract_value)}
                              </div>
                              <div className="col-span-2 text-sm">
                                {p.auto_renew ? (
                                  <Badge variant="secondary" className="text-xs">Auto-Renew</Badge>
                                ) : (
                                  formatDate(p.end_date)
                                )}
                              </div>
                              <div className="col-span-1 text-right">
                                <Button asChild variant="ghost" size="sm" data-testid={`rep-open-project-${p.id}`}>
                                  <Link to={`/orders/${p.id}`}>Open</Link>
                                </Button>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
